import { defineStore } from 'pinia';
import type { PersonBasicData, PersonFinancialData, ExtendedDataStatus } from '@/types/person';
import personApi, { type ExtendedDataProductKey } from '@/api/modules/personApi';
import { DATA_UNIT_PRODUCT_KEYS } from '@/constants/dataUnitProducts';
import { PersonBasicDataProviderError } from '@/types/enums';
import { getApiErrorCode } from '@/utils/apiErrorMessage';

export const usePersonStore = defineStore('person', {
  state: () => ({
    // Current person UUID being viewed
    currentUuid: null as string | null,
    generalData: null as PersonBasicData | null,
    loading: false,
    error: null as string | null,
    // Set when the data provider answered this person's basic data with an error (e.g. a minor)
    basicDataProviderError: null as PersonBasicDataProviderError | null,
    // Basic data access status
    hasBasicDataAccess: null as boolean | null,
    basicDataStatusLoading: false,
    requestingBasicData: false,
    cpf: null as string | null,
    // Extended data status
    extendedDataStatus: null as ExtendedDataStatus | null,
    extendedDataStatusLoading: false,
    requestingExtendedData: false,
    requestingDatasets: [] as ExtendedDataProductKey[],
    // Estimated income and total assets (banner), a data unit bought separately
    financialDataAccess: null as boolean | null,
    financialData: null as PersonFinancialData | null,
    financialDataLoading: false,
    requestingFinancialData: false,
    // Cache flags to track what's been fetched for current UUID
    basicDataStatusFetched: false,
    generalDataFetched: false,
    extendedDataStatusFetched: false,
    financialDataFetched: false,
  }),

  getters: {
    hasGeneralData: (state) => !!state.generalData,
    hasAddressData: (state) => state.extendedDataStatus?.has_addresses || state.generalData?.hasAddressData || false,
    hasPhonesData: (state) => state.extendedDataStatus?.has_phones || state.generalData?.hasPhonesData || false,
    hasEmailsData: (state) => state.extendedDataStatus?.has_emails || state.generalData?.hasEmailsData || false,
  },

  actions: {
    // Call this synchronously as soon as the route uuid changes, before any request goes out.
    // The fetch actions reset the cache too, but only once they run — which is too late for
    // components that render (and mount children) off the previous person's flags.
    prepareForUuid(uuid: string) {
      this._resetCacheIfNewUuid(uuid);
    },

    // Reset cache when switching to a different person
    _resetCacheIfNewUuid(uuid: string) {
      if (this.currentUuid !== uuid) {
        this.currentUuid = uuid;
        this.generalData = null;
        this.hasBasicDataAccess = null;
        this.cpf = null;
        this.extendedDataStatus = null;
        this.financialDataAccess = null;
        this.financialData = null;
        this.error = null;
        this.basicDataProviderError = null;
        this.basicDataStatusFetched = false;
        this.generalDataFetched = false;
        this.extendedDataStatusFetched = false;
        this.financialDataFetched = false;
      }
    },

    async fetchGeneralData(uuid: string, forceRefresh = false) {
      // Skip if already loading for this UUID (prevents concurrent duplicate calls)
      // Check this FIRST before any state changes
      if (this.loading && this.currentUuid === uuid && !forceRefresh) {
        return;
      }

      // Skip if already fetched for this UUID and not forcing refresh.
      // A prior failed attempt (error set) also counts as "attempted": otherwise a component
      // that fetches on mount would keep re-firing on every remount while the request fails,
      // spinning in a mount/fetch/unmount loop. Callers force a real retry with forceRefresh.
      if (this.currentUuid === uuid && (this.generalDataFetched || this.error) && !forceRefresh) {
        return;
      }

      this._resetCacheIfNewUuid(uuid);

      this.loading = true;
      this.error = null;
      this.basicDataProviderError = null;

      try {
        this.generalData = await personApi.getBasicData(uuid);
        this.error = null;
        this.generalDataFetched = true;
      } catch (err: any) {
        this.error = err.message || 'Ocorreu um erro ao buscar os dados da pessoa.';
        const errorCode = getApiErrorCode(err);
        this.basicDataProviderError = Object.values(PersonBasicDataProviderError).find((code) => code === errorCode) ?? null;
        this.generalData = null;
      } finally {
        this.loading = false;
      }
    },

    // Checks whether the account bought the income/assets data unit and, if so, loads it.
    async syncFinancialData(uuid: string, forceRefresh = false) {
      if (this.currentUuid === uuid && !forceRefresh && (this.financialDataLoading || this.financialDataFetched)) {
        return;
      }

      this._resetCacheIfNewUuid(uuid);

      this.financialDataLoading = true;
      try {
        const { has_access } = await personApi.getFinancialDataStatus(uuid);
        const financialData = has_access ? await personApi.getFinancialData(uuid) : null;
        // Drop the answer if the user navigated to another person meanwhile.
        if (this.currentUuid !== uuid) return;
        this.financialDataAccess = has_access;
        this.financialData = financialData;
        this.financialDataFetched = true;
      } catch (err: any) {
        // Income and assets are secondary banner info: on failure the banner just omits them.
        console.error('Error fetching person financial data:', err);
        if (this.currentUuid === uuid) {
          this.financialDataAccess = null;
          this.financialData = null;
        }
      } finally {
        this.financialDataLoading = false;
      }
    },

    async requestFinancialData(uuid: string) {
      this.requestingFinancialData = true;
      try {
        await personApi.requestFinancialData(uuid);
        await this.syncFinancialData(uuid, true);
      } finally {
        this.requestingFinancialData = false;
      }
    },

    async checkBasicDataStatus(uuid: string, forceRefresh = false) {
      // Skip if already loading for this UUID (prevents concurrent duplicate calls)
      if (this.basicDataStatusLoading && this.currentUuid === uuid && !forceRefresh) {
        return;
      }

      // Skip if already fetched for this UUID and not forcing refresh
      if (this.currentUuid === uuid && this.basicDataStatusFetched && !forceRefresh) {
        return;
      }

      this._resetCacheIfNewUuid(uuid);

      this.basicDataStatusLoading = true;
      try {
        const response = await personApi.getBasicDataStatus(uuid);
        this.hasBasicDataAccess = response.has_access;
        this.cpf = response.cpf;
        this.basicDataStatusFetched = true;
      } catch (err: any) {
        console.error('Error checking basic data status:', err);
        this.hasBasicDataAccess = null;
      } finally {
        this.basicDataStatusLoading = false;
      }
    },

    async requestBasicData(uuid: string) {
      this.requestingBasicData = true;
      try {
        await personApi.requestBasicData(uuid);
        // After successful request, refresh the status (force refresh)
        await this.checkBasicDataStatus(uuid, true);
        return true;
      } catch (err: any) {
        console.error('Error requesting basic data:', err);
        return false;
      } finally {
        this.requestingBasicData = false;
      }
    },

    async checkExtendedDataStatus(uuid: string, forceRefresh = false) {
      // Skip if already loading for this UUID (prevents concurrent duplicate calls)
      if (this.extendedDataStatusLoading && this.currentUuid === uuid && !forceRefresh) {
        return;
      }

      // Skip if already fetched for this UUID and not forcing refresh
      if (this.currentUuid === uuid && this.extendedDataStatusFetched && !forceRefresh) {
        return;
      }

      this._resetCacheIfNewUuid(uuid);

      this.extendedDataStatusLoading = true;
      try {
        this.extendedDataStatus = await personApi.getExtendedDataStatus(uuid);
        this.extendedDataStatusFetched = true;
      } catch (err: any) {
        console.error('Error checking extended data status:', err);
        this.extendedDataStatus = null;
      } finally {
        this.extendedDataStatusLoading = false;
      }
    },

    async requestExtendedData(uuid: string, datasets: ExtendedDataProductKey[]) {
      this.requestingExtendedData = true;
      this.requestingDatasets = datasets;
      try {
        await personApi.requestExtendedData(uuid, datasets);
        // After successful request, refresh the general data to include the new extended data (force refresh)
        await this.fetchGeneralData(uuid, true);
        // Also refresh the extended data status (force refresh)
        await this.checkExtendedDataStatus(uuid, true);
      } finally {
        this.requestingExtendedData = false;
        this.requestingDatasets = [];
      }
    },

    isRequestingDataset(dataset: ExtendedDataProductKey): boolean {
      return this.requestingExtendedData && this.requestingDatasets.includes(dataset);
    },

    isRequestingAddressData(): boolean {
      return this.isRequestingDataset(DATA_UNIT_PRODUCT_KEYS.personAddresses);
    },

    isRequestingPhoneData(): boolean {
      return this.isRequestingDataset(DATA_UNIT_PRODUCT_KEYS.personPhones);
    },

    isRequestingEmailData(): boolean {
      return this.isRequestingDataset(DATA_UNIT_PRODUCT_KEYS.personEmails);
    },

    // Method to clear all cached data (useful when navigating away from person pages)
    clearCache() {
      this.currentUuid = null;
      this.generalData = null;
      this.financialDataAccess = null;
      this.financialData = null;
      this.hasBasicDataAccess = null;
      this.cpf = null;
      this.extendedDataStatus = null;
      this.error = null;
      this.basicDataStatusFetched = false;
      this.generalDataFetched = false;
      this.financialDataFetched = false;
      this.extendedDataStatusFetched = false;
    },
  },
});
